import { alpha, Box, styled, Typography } from "@mui/material";

import { BasicButton } from "components/buttons/basic-button";
import { CustomizedPopover } from "components/popover";

import { AssistantActionButton } from "../../styles";
import type {
  StyledAssistantWalkthroughSectionAssistantStepControlsProps,
  StyledAssistantWalkthroughSectionAssistantWalkthroughLayoutProps,
} from "./types";
import { IAssistantProgressSegmentProps } from "./types";

export const AssistantWalkthroughLayout = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(5),
  flex: 1,
}));

export const AssistantStepHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  color: theme.palette.custom.semantic.text.muted,
}));

export const AssistantProgress = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
}));

export const AssistantProgressSegment = styled(Box, {
  shouldForwardProp: (prop) => prop !== "filled",
})<IAssistantProgressSegmentProps>(({ theme, filled }) => ({
  flex: 1,
  height: theme.spacing(1),
  borderRadius: theme.shape.borderRadius[1],
  backgroundColor: filled
    ? theme.palette.secondary.main
    : theme.palette.custom.semantic.border.default,
}));

export const AssistantStepWaiting = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  color: theme.palette.custom.semantic.text.muted,
  "&::before": {
    content: '""',
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginTop: theme.spacing(1),
    flexShrink: 0,
    borderRadius: theme.shape.borderRadius[5],
    backgroundColor: theme.palette.secondary.main,
  },
}));

export const AssistantStepContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  "& > p": { lineHeight: 1.7 },
}));

export const AssistantStepControls = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(2),
}));

export const AssistantCancelButton = styled(AssistantActionButton)(
  ({ theme }) => ({
    marginLeft: "auto",
    padding: theme.spacing(2, 11),
    color: theme.palette.custom.semantic.text.muted,
  })
);

export const AssistantMoreButton = styled(BasicButton)(({ theme }) => ({
  width: theme.spacing(7),
  minWidth: theme.spacing(7),
  height: theme.spacing(7),
  padding: theme.spacing(1),
  flexShrink: 0,
}));

export const AssistantStepOptions = styled(CustomizedPopover)(({ theme }) => ({
  zIndex: theme.zIndex.modal + 3,
}));

export const AssistantStepOption = styled(BasicButton)(({ theme }) => ({
  ...theme.typography.subtitle8,
  justifyContent: "flex-start",
  padding: theme.spacing(3, 4),
  borderRadius: 0,
}));

export const AssistantScenarioReturn = styled(AssistantActionButton)(
  ({ theme }) => ({
    alignSelf: "flex-start",
    padding: 0,
    minWidth: 0,
    color: theme.palette.custom.semantic.text.muted,
  })
);

export const AssistantScenarioSelection = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(8),
  padding: theme.spacing(3, 11),
  borderRadius: `${theme.shape.borderRadius[2]} 0 ${theme.shape.borderRadius[2]} ${theme.shape.borderRadius[2]}`,
  backgroundColor: alpha(
    theme.palette.secondary.main,
    theme.palette.action.selectedOpacity
  ),
}));

export const AssistantScenarioNote = styled(Typography)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.custom.semantic.border.subtle}`,
  paddingTop: theme.spacing(11),
  color: theme.palette.custom.semantic.text.muted,
}));

export const StyledAssistantWalkthroughSectionAssistantWalkthroughLayout =
  styled(AssistantWalkthroughLayout, {
    shouldForwardProp: (prop) => !["isScenario"].includes(String(prop)),
  })<StyledAssistantWalkthroughSectionAssistantWalkthroughLayoutProps>(
    ({ theme, isScenario }) => ({
      gap: isScenario ? theme.spacing(3) : theme.spacing(5),
    })
  );

export const StyledAssistantWalkthroughSectionBoxDisplay = styled(Box)(
  ({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  })
);

export const StyledAssistantWalkthroughSectionTypographyWhiteSpace = styled(
  Typography
)({
  whiteSpace: "nowrap",
});

export const StyledAssistantWalkthroughSectionAssistantStepControls = styled(
  AssistantStepControls,
  { shouldForwardProp: (prop) => !["isScenario"].includes(String(prop)) }
)<StyledAssistantWalkthroughSectionAssistantStepControlsProps>(
  ({ theme, isScenario }) => ({
    marginTop: isScenario ? theme.spacing(3) : theme.spacing(0),
    marginBottom: isScenario ? theme.spacing(3) : theme.spacing(0),
  })
);
